import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { Close as CloseIcon, RestoreFromTrash, Search } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { ThemeContext } from '../ThemeContext/ThemeContext';
import http from '../../services/http';

/**
 * A lixeira de chamados (Gisele, pontos 8 e 9 de 27/10/2026).
 *
 * "Criar uma área de consulta dos tickets excluídos, porque um ticket pode ser
 * removido por engano" e "essa área funciona como lixeira: o Administrador
 * pode restaurar o ticket quando precisar."
 *
 * ## Por que um diálogo, e não uma página
 *
 * Página nova precisa de rota, e rota nova precisa de entrada no
 * `PermissionService` **e** de backfill no banco -- sem os dois, a tela não
 * aparece para ninguém. O diálogo fica onde os chamados estão, no menu
 * "Opções" da listagem, e aparece só para quem pode excluir: quem pode
 * destruir tem de poder desfazer.
 *
 * ## A busca é do SERVIDOR
 *
 * A lixeira cresce sem teto e ninguém a esvazia. Filtrar no navegador sobre a
 * primeira página mostraria "nada encontrado" para um chamado que está lá --
 * o mesmo defeito que a busca da listagem já teve.
 */
const LixeiraDeChamados = ({ open, onClose, onRestaurado }) => {
    const { theme } = useContext(ThemeContext);
    const [linhas, setLinhas] = useState([]);
    const [total, setTotal] = useState(0);
    const [carregando, setCarregando] = useState(false);
    const [busca, setBusca] = useState('');
    const [termo, setTermo] = useState('');

    const carregar = useCallback(() => {
        setCarregando(true);

        http.get('/ticket/lixeira', { params: { per_page: 50, search: termo || undefined } })
            .then((res) => {
                setLinhas(res.data?.data || []);
                setTotal(res.data?.total || 0);
            })
            .catch(() => toast.error('Não foi possível carregar a lixeira.'))
            .finally(() => setCarregando(false));
    }, [termo]);

    useEffect(() => {
        if (open) carregar();
    }, [open, carregar]);

    const restaurar = (chamado) => {
        http.post(`/ticket/lixeira/${chamado.id}/restaurar`)
            .then(() => {
                toast.success('Chamado restaurado.');
                carregar();
                if (onRestaurado) onRestaurado(chamado);
            })
            .catch(() => toast.error('Não foi possível restaurar o chamado.'));
    };

    const dataHora = (valor) => (valor ? new Date(valor).toLocaleString('pt-BR') : '—');

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>Lixeira de chamados</span>
                <IconButton onClick={onClose} aria-label="Fechar">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                    Chamados excluídos continuam aqui, com os comentários e os clientes vinculados.
                    Restaurar devolve o chamado inteiro à listagem.
                </Typography>

                <Box
                    component="form"
                    onSubmit={(e) => { e.preventDefault(); setTermo(busca.trim()); }}
                    sx={{ display: 'flex', gap: 1, mb: 2 }}
                >
                    <TextField
                        size="small"
                        label="Procurar por número, título ou cliente"
                        value={busca}
                        onChange={(e) => setBusca(e.target.value)}
                        sx={{ flex: 1 }}
                    />
                    <Button type="submit" variant="outlined" startIcon={<Search />}>
                        Procurar
                    </Button>
                </Box>

                <Box sx={{ height: 3, mb: 1 }}>
                    {carregando && <LinearProgress data-testid="carregando-lixeira" sx={{ height: 3 }} />}
                </Box>

                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    {total === 0 ? 'Nenhum chamado na lixeira.' : `${total} chamado(s) na lixeira`}
                </Typography>

                <Table size="small" data-testid="lista-da-lixeira">
                    <TableHead>
                        <TableRow>
                            <TableCell>Número</TableCell>
                            <TableCell>Título</TableCell>
                            <TableCell>Cliente</TableCell>
                            <TableCell>Excluído por</TableCell>
                            <TableCell>Quando</TableCell>
                            <TableCell align="right">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {linhas.map((item) => (
                            <TableRow key={item.id} hover>
                                <TableCell sx={{ color: theme.palette.text.primary }}>{item.number}</TableCell>
                                <TableCell sx={{ color: theme.palette.text.primary }}>{item.title}</TableCell>
                                <TableCell sx={{ color: theme.palette.text.secondary }}>{item.client || '—'}</TableCell>
                                <TableCell sx={{ color: theme.palette.text.secondary }}>
                                    {item.quem_excluiu?.name || '—'}
                                </TableCell>
                                <TableCell sx={{ color: theme.palette.text.secondary }}>
                                    {dataHora(item.deleted_at)}
                                </TableCell>
                                <TableCell align="right">
                                    <Tooltip title="Restaurar" arrow>
                                        <IconButton
                                            size="small"
                                            aria-label={`Restaurar chamado ${item.number}`}
                                            onClick={() => restaurar(item)}
                                        >
                                            <RestoreFromTrash fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </DialogContent>
        </Dialog>
    );
};

export default LixeiraDeChamados;
